import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Drawer } from '../../components/Drawer/Drawer';
import { useToast } from '../../components/Toast';
import { errorMessage } from '../../lib/errorMessage';
import { manualFromRange } from './hoursMath';
import { useUpsertTimeEntry, type TimeEntry } from './hoursQueries';
import styles from './hours.module.css';

const FORM_ID = 'time-entry-form';

const schema = z
  .object({
    date: z.string().min(1, 'שדה חובה'),
    start_time: z.string().min(1, 'שדה חובה'),
    hours: z.number({ invalid_type_error: 'מספר' }).min(0).max(24),
    minutes: z.number({ invalid_type_error: 'מספר' }).min(0).max(59),
    project_id: z.string(),
    description: z.string().max(1000, 'עד 1000 תווים'),
    billable: z.boolean(),
  })
  .refine((v) => v.hours * 60 + v.minutes > 0, {
    message: 'משך העבודה חייב להיות יותר מאפס',
    path: ['minutes'],
  });

type FormValues = z.infer<typeof schema>;

function numberOrNaN(v: unknown): number {
  return v === '' || v === null ? Number.NaN : Number(v);
}

interface Props {
  open: boolean;
  entry: TimeEntry | null;
  /** A new entry opened from a project starts on that project. */
  defaultProjectId?: string;
  projects: { id: string; name: string }[];
  orgId: string | null;
  userId: string | null;
  onClose: () => void;
}

export function TimeEntryDrawer({
  open,
  entry,
  defaultProjectId,
  projects,
  orgId,
  userId,
  onClose,
}: Props) {
  const toast = useToast();
  const upsert = useUpsertTimeEntry(orgId, userId);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!open) return;
    if (entry) {
      reset({
        ...manualFromRange(entry.started_at, entry.ended_at),
        project_id: entry.project_id ?? '',
        description: entry.description ?? '',
        billable: entry.billable,
      });
    } else {
      reset({
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00',
        hours: 1,
        minutes: 0,
        project_id: defaultProjectId ?? '',
        description: '',
        billable: true,
      });
    }
  }, [open, entry, defaultProjectId, reset]);

  async function onSubmit(values: FormValues) {
    try {
      await upsert.mutateAsync({
        id: entry?.id ?? null,
        values: { ...values, project_id: values.project_id || null },
      });
      toast.success(entry ? 'הרישום עודכן' : 'השעות נרשמו');
      onClose();
    } catch (err) {
      toast.error(`לא הצלחנו לשמור · ${errorMessage(err)}`);
    }
  }

  const running = entry !== null && entry.ended_at === null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="שעות"
      title={entry ? 'עריכת רישום שעות' : 'הוסף שעות'}
      ariaLabel={entry ? 'עריכת רישום שעות' : 'הוסף שעות'}
      footer={
        <>
          <button type="button" className={styles.btnSecondary} onClick={onClose}>
            בטל
          </button>
          <button
            type="submit"
            form={FORM_ID}
            className={styles.btnPrimary}
            disabled={upsert.isPending || running}
          >
            שמור
          </button>
        </>
      }
    >
      {running ? (
        <p className={styles.hint}>הטיימר עדיין רץ. עצור אותו כדי לערוך את הרישום.</p>
      ) : null}
      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label htmlFor="te-date">תאריך</label>
            <input
              id="te-date"
              type="date"
              dir="ltr"
              className={styles.input}
              {...register('date')}
            />
            {errors.date ? <p className={styles.error}>{errors.date.message}</p> : null}
          </div>
          <div className={styles.field}>
            <label htmlFor="te-start">שעת התחלה</label>
            <input
              id="te-start"
              type="time"
              dir="ltr"
              className={styles.input}
              {...register('start_time')}
            />
          </div>
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label htmlFor="te-hours">שעות</label>
            <input
              id="te-hours"
              type="number"
              min={0}
              max={24}
              step="0.25"
              dir="ltr"
              className={styles.input}
              {...register('hours', { setValueAs: numberOrNaN })}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="te-minutes">דקות</label>
            <input
              id="te-minutes"
              type="number"
              min={0}
              max={59}
              step="5"
              dir="ltr"
              className={styles.input}
              {...register('minutes', { setValueAs: numberOrNaN })}
            />
          </div>
        </div>
        {errors.minutes || errors.hours ? (
          <p className={styles.error}>{errors.minutes?.message ?? errors.hours?.message}</p>
        ) : (
          <p className={styles.hint}>כמה זמן עבדת. אפשר שעות עם שבר, למשל 1.5.</p>
        )}

        <div className={styles.field}>
          <label htmlFor="te-project">פרויקט</label>
          <select id="te-project" className={styles.input} {...register('project_id')}>
            <option value="">ללא פרויקט</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="te-description">על מה עבדת</label>
          <input
            id="te-description"
            type="text"
            className={styles.input}
            {...register('description')}
          />
          {errors.description ? <p className={styles.error}>{errors.description.message}</p> : null}
        </div>

        <label className={styles.checkboxLabel}>
          <input type="checkbox" {...register('billable')} />
          לחיוב
        </label>
      </form>
    </Drawer>
  );
}
